import type { PuckComponent, PuckData } from "../ai/puck-types.js";
import {
  AMP_COMPONENT_REGISTRY,
  AMP_COMPONENT_TYPES,
  type AmpComponentConfig,
  type AmpComponentType,
} from "./amp-components.js";
import {
  renderAmpEmailHtml,
  renderAmpFallbackHtml,
  type AmpEmailComponent,
  type AmpRenderOptions,
} from "./amp-renderer.js";
import {
  EMAIL_COMPONENT_REGISTRY,
  EMAIL_COMPONENT_TYPES,
  type EmailComponentConfig,
  type EmailComponentType,
} from "./email-components.js";
import {
  renderEmailHtml,
  type EmailComponent,
  type EmailRenderableNode,
  type EmailRenderOptions,
} from "./email-renderer.js";

export const EMAIL_MODE_COMPONENT_TYPES = [
  ...EMAIL_COMPONENT_TYPES,
  ...AMP_COMPONENT_TYPES,
] as const;

export type EmailModeComponentType = (typeof EMAIL_MODE_COMPONENT_TYPES)[number];

export type EmailModeComponentRegistryItem =
  | (EmailComponentConfig & { channel: "standard" })
  | (AmpComponentConfig & { channel: "amp" });

export interface EmailModePuckComponent extends PuckComponent {
  type: EmailModeComponentType;
}

export interface EmailModeOutputContract {
  puckData: PuckData;
  content: EmailModePuckComponent[];
  standardRenderNodes: EmailRenderableNode[];
  standardComponents: EmailComponent[];
  ampComponents: AmpEmailComponent[];
  standardFallbackRows: string[];
}

export interface RenderEmailModeOutputOptions {
  email?: EmailRenderOptions;
  amp?: AmpRenderOptions;
}

export interface RenderedEmailModeOutput {
  contract: EmailModeOutputContract;
  html: string;
  ampHtml: string | null;
  hasAmpComponents: boolean;
}

export const EMAIL_MODE_COMPONENT_REGISTRY: readonly EmailModeComponentRegistryItem[] = [
  ...EMAIL_COMPONENT_REGISTRY.map((component) => ({
    ...component,
    channel: "standard" as const,
  })),
  ...AMP_COMPONENT_REGISTRY.map((component) => ({
    ...component,
    channel: "amp" as const,
  })),
];

const EMAIL_MODE_COMPONENT_REGISTRY_MAP = new Map(
  EMAIL_MODE_COMPONENT_REGISTRY.map((component) => [component.type, component]),
);

function cloneProps<T extends Record<string, unknown>>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function createComponentId(type: EmailModeComponentType): string {
  const cryptoApi = globalThis as { crypto?: { randomUUID?: () => string } };
  const randomId = cryptoApi.crypto?.randomUUID?.() ?? `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  return `${type}_${randomId}`;
}

function normalizeContent(
  input: PuckData | readonly PuckComponent[],
): EmailModePuckComponent[] {
  const content: readonly PuckComponent[] = "content" in input ? input.content : input;

  return content.filter((component): component is EmailModePuckComponent =>
    isEmailModeComponentType(component.type),
  );
}

export function isEmailModeComponentType(value: string): value is EmailModeComponentType {
  return EMAIL_MODE_COMPONENT_REGISTRY_MAP.has(value as EmailModeComponentType);
}

export function isAmpEmailModeComponentType(value: string): value is AmpComponentType {
  return (AMP_COMPONENT_TYPES as readonly string[]).includes(value);
}

export function isStandardEmailModeComponentType(value: string): value is EmailComponentType {
  return (EMAIL_COMPONENT_TYPES as readonly string[]).includes(value);
}

export function getEmailModeComponentConfig(
  type: EmailModeComponentType,
): EmailModeComponentRegistryItem | undefined {
  return EMAIL_MODE_COMPONENT_REGISTRY_MAP.get(type);
}

export function createEmailModeComponent(type: EmailModeComponentType): EmailModePuckComponent {
  const config = getEmailModeComponentConfig(type);
  if (!config) {
    throw new Error(`Unsupported email-mode component type: ${type}`);
  }

  const id = createComponentId(type);

  return {
    _id: id,
    type,
    props: {
      id,
      ...cloneProps(config.defaultProps),
    },
  };
}

export function adaptPuckDataToEmailOutputContract(
  input: PuckData | readonly PuckComponent[],
): EmailModeOutputContract {
  const content = normalizeContent(input);
  const standardRenderNodes: EmailRenderableNode[] = [];
  const standardComponents: EmailComponent[] = [];
  const ampComponents: AmpEmailComponent[] = [];
  const standardFallbackRows: string[] = [];

  for (const component of content) {
    if (isStandardEmailModeComponentType(component.type)) {
      const standardComponent: EmailComponent = {
        type: component.type,
        props: component.props,
      };
      standardComponents.push(standardComponent);
      standardRenderNodes.push(standardComponent);
      continue;
    }

    if (isAmpEmailModeComponentType(component.type)) {
      const ampComponent: AmpEmailComponent = {
        type: component.type,
        props: component.props,
      };
      ampComponents.push(ampComponent);
      const fallbackRow = renderAmpFallbackHtml(ampComponent);
      standardFallbackRows.push(fallbackRow);
      standardRenderNodes.push({ rawHtml: fallbackRow });
    }
  }

  return {
    puckData: Array.isArray(input) ? { content } : { ...input, content },
    content,
    standardRenderNodes,
    standardComponents,
    ampComponents,
    standardFallbackRows,
  };
}

export function renderEmailModeOutput(
  input: PuckData | readonly PuckComponent[],
  options: RenderEmailModeOutputOptions = {},
): RenderedEmailModeOutput {
  const contract = adaptPuckDataToEmailOutputContract(input);
  const html = renderEmailHtml(contract.standardRenderNodes, options.email);
  const ampHtml = contract.ampComponents.length > 0
    ? renderAmpEmailHtml(contract.ampComponents, options.amp)
    : null;

  return {
    contract,
    html,
    ampHtml,
    hasAmpComponents: contract.ampComponents.length > 0,
  };
}
